import * as React from 'react'
import Button from '@mui/material/Button'
import ButtonGroup from '@mui/material/ButtonGroup'
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown'
import ClickAwayListener from '@mui/material/ClickAwayListener'
import Grow from '@mui/material/Grow'
import Paper from '@mui/material/Paper'
import Popper from '@mui/material/Popper'
import MenuItem from '@mui/material/MenuItem'
import MenuList from '@mui/material/MenuList'
import { useRef, useState } from 'react'
import { colorPalette } from '../../../style/colorPalette'

export interface SplitButtonProps {
  disabled?: boolean
  options: SplitButtonOptions[]
  buttonStyle?: React.CSSProperties
}

export interface SplitButtonOptions {
  key: string
  onClick: () => any
}

export default function SplitButton(props: SplitButtonProps) {
  const { options, disabled, buttonStyle } = props
  const [open, setOpen] = useState(false)
  const anchorRef = useRef<HTMLDivElement>(null)
  const [selectedIndex, setSelectedIndex] = useState(0)

  const colorStyle = {
    borderColor: colorPalette.monotone[3],
    backgroundColor: colorPalette.monotone[1],
    color: colorPalette.monotone[7],
    '&:hover': {
      backgroundColor: colorPalette.monotone[1],
      borderColor: colorPalette.monotone[4],
    },
  }

  const handleMenuItemClick = (index: number) => {
    setSelectedIndex(index)
    setOpen(false)
    options[index].onClick?.()
  }

  const handleToggle = () => {
    setOpen(prevOpen => !prevOpen)
  }

  const handleClose = (event: MouseEvent | TouchEvent) => {
    if (
      anchorRef.current &&
      anchorRef.current.contains(event.target as HTMLElement)
    ) {
      return
    }

    setOpen(false)
  }

  const onClick = React.useCallback(() => {
    options[selectedIndex].onClick?.()
  }, [options, selectedIndex])

  return (
    <React.Fragment>
      <ButtonGroup
        variant="outlined"
        ref={anchorRef}
        sx={{
          marginLeft: '20px',
          boxShadow: 'none',
          height: '32px',
          '& .MuiButtonGroup-grouped:not(:last-of-type)': {
            borderColor: disabled
              ? `${colorPalette.monotone[2]} !important`
              : `${colorPalette.monotone[3]} !important`,
          },
          ...buttonStyle,
        }}
      >
        <Button
          onClick={onClick}
          disabled={props.disabled}
          sx={{
            ...colorStyle,
            padding: '4px 8px',
            height: '32px',
            ...buttonStyle,
          }}
        >
          {options[selectedIndex].key}
        </Button>
        <Button
          onClick={handleToggle}
          disabled={disabled}
          sx={{
            ...colorStyle,
            height: '32px',
            ...buttonStyle,
          }}
        >
          <ArrowDropDownIcon />
        </Button>
      </ButtonGroup>
      <Popper
        open={open}
        anchorEl={anchorRef.current}
        placement="bottom-end"
        transition={true}
        disablePortal={true}
      >
        {({ TransitionProps, placement }) => (
          <Grow
            {...TransitionProps}
            style={{
              transformOrigin:
                placement === 'bottom' ? 'right top' : 'right bottom',
            }}
          >
            <Paper>
              <ClickAwayListener onClickAway={handleClose}>
                <MenuList>
                  {options.map((option, index) => (
                    <MenuItem
                      key={option.key}
                      selected={index === selectedIndex}
                      onClick={() => handleMenuItemClick(index)}
                      sx={{
                        justifyContent: 'center',
                        textAlign: 'center',
                      }}
                    >
                      {option.key}
                    </MenuItem>
                  ))}
                </MenuList>
              </ClickAwayListener>
            </Paper>
          </Grow>
        )}
      </Popper>
    </React.Fragment>
  )
}
